import { DialogRef } from '@angular/cdk/dialog';
import { Component, computed, inject, OnInit, signal } from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslocoModule } from '@jsverse/transloco';
import {
  LoginFlow,
  Session,
  UiText,
  UpdateLoginFlowBody,
  UpdateLoginFlowWithPasswordMethod,
} from '@ory/client';
import { VxButtonComponent } from '@ui/button/button.component';
import { VxDialogComponent } from '@ui/dialog/dialog.component';
import { VxDialogFooterComponent } from '@ui/dialog/dialog-footer.component';
import { VxDialogHeaderComponent } from '@ui/dialog/dialog-header.component';
import { VxFormFieldComponent } from '@ui/form-field/form-field.component';
import { VxInputComponent } from '@ui/input/input.component';
import { VxSkeletonComponent } from '@ui/skeleton/skeleton.component';
import {
  csrfToken,
  flowMessages,
  offersInput,
  submitFlow,
} from 'src/app/services/authentication/kratos-flow';
import { KratosAuthenticationService } from 'src/app/services/authentication/kratos.service';
import { TranslationService } from 'src/app/services/translation/translation.service';
import { CodeEntryComponent } from '../../components/code-entry/code-entry.component';
import { codeStep } from '../../components/code-entry/code-step';
import { KratosFormV2Component } from '../../components/kratos-form/form.component';
import { KratosMessagesComponent } from '../../components/kratos-messages/kratos-messages.component';

/** How the person proves it's them: what their account offers. */
type ReauthStep = 'password' | 'send-code' | 'code' | 'aal2' | 'failed';

/**
 * Asks the person to confirm it's them before a privileged settings change:
 * their password, or — for accounts without one (email-code or social
 * sign-up) — a code mailed to them. With a second factor set up, that is
 * asked instead. Opened through `VxDialogService`; closes with `true` once
 * Kratos accepts.
 */
@Component({
  selector: 'app-dialog-reauthenticate',
  templateUrl: './reauthenticate.component.html',
  imports: [
    ReactiveFormsModule,
    TranslocoModule,
    CodeEntryComponent,
    KratosFormV2Component,
    KratosMessagesComponent,
    VxButtonComponent,
    VxDialogComponent,
    VxDialogFooterComponent,
    VxDialogHeaderComponent,
    VxFormFieldComponent,
    VxInputComponent,
    VxSkeletonComponent,
  ],
  host: { class: 'block w-full' },
})
export class ReAuthenticateDialogComponent implements OnInit {
  private readonly dialogRef = inject<DialogRef<boolean>>(DialogRef);
  private readonly kratos = inject(KratosAuthenticationService);
  private readonly translation = inject(TranslationService);

  protected readonly loginFlow = signal<LoginFlow | undefined>(undefined);

  protected readonly step = signal<ReauthStep | undefined>(undefined);

  /** The account's address: the identifier, and where a code goes. */
  protected readonly email = signal('');

  /** The account has a password: a code is the alternative, not the only way. */
  protected readonly hasPassword = computed(() => {
    const flow = this.loginFlow();
    return !!flow && offersInput(flow, 'password', 'password');
  });

  protected readonly offersCode = computed(() => {
    const flow = this.loginFlow();
    return !!flow && !!flow.ui.nodes.some(node => node.group === 'code');
  });

  protected readonly messages = signal<UiText[]>([]);

  /** The code boxes; their busy state covers the password and resend too. */
  protected readonly codeEntry = codeStep();

  protected readonly passwordForm = new FormGroup({
    password: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
  });

  ngOnInit(): Promise<void> {
    return this.start();
  }

  /** Reads the session and opens the matching login flow; retried on failure. */
  protected async start(): Promise<void> {
    this.step.set(undefined);
    try {
      const session = await this.kratos.whoami();
      this.email.set(
        (session.identity?.traits as { email?: string } | undefined)?.email ??
          ''
      );
      // a configured second factor means Kratos expects aal2 here
      const aal2 = !!session.authentication_methods?.some(
        method => method.aal === 'aal2'
      );
      const flow = await this.kratos.createLoginFlow(aal2 ? 'aal2' : 'aal1', {
        refresh: true,
      });
      this.loginFlow.set(flow);
      if (aal2) {
        this.step.set('aal2');
      } else {
        this.step.set(this.hasPassword() ? 'password' : 'send-code');
      }
    } catch {
      // network or Kratos down: say so instead of loading forever
      this.step.set('failed');
    }
  }

  protected async submitPassword(): Promise<void> {
    const flow = this.loginFlow();
    const { password } = this.passwordForm.controls;
    if (!flow) {
      return;
    }
    if (password.invalid) {
      password.markAsTouched();
      return;
    }
    await this.codeEntry.run(() =>
      this.submit({
        method: 'password',
        identifier: this.email(),
        password: password.value,
        csrf_token: csrfToken(flow),
      })
    );
  }

  /** Mails a code; Kratos answers with the flow that takes it (a 400). */
  protected async sendCode(): Promise<void> {
    const flow = this.loginFlow();
    if (!flow) {
      return;
    }
    this.messages.set([]);
    this.codeEntry.reset();
    await this.codeEntry.run(() =>
      this.submit({
        method: 'code',
        identifier: this.email(),
        csrf_token: csrfToken(flow),
        transient_payload: {
          flow_id: flow.id,
          locale: this.translation.currentLocale,
        },
      })
    );
    if (this.loginFlow()?.state === 'sent_email') {
      this.step.set('code');
    }
  }

  protected async submitCode(code: string): Promise<void> {
    const flow = this.loginFlow();
    if (!flow) {
      return;
    }
    const accepted = await this.codeEntry.verify(() =>
      this.submit({
        method: 'code',
        code,
        identifier: this.email(),
        csrf_token: csrfToken(flow),
      })
    );
    // a failed submit left the code step for "failed" already
    if (accepted === false && this.step() === 'code') {
      this.codeEntry.reject();
    }
  }

  protected useCode(): void {
    this.messages.set([]);
    this.step.set('send-code');
  }

  protected usePassword(): void {
    this.messages.set([]);
    this.step.set('password');
  }

  /** The second factor, through Kratos's own form (TOTP, security key). */
  protected submitSecondFactor(body: UpdateLoginFlowWithPasswordMethod): void {
    this.codeEntry.run(() => this.submit(body as UpdateLoginFlowBody));
  }

  protected close(): void {
    this.dialogRef.close(false);
  }

  /**
   * Closes on success; otherwise keeps Kratos's re-rendered flow. Without
   * one (network, the flow expired while the code was on its way) the
   * dialog offers a fresh start.
   */
  private async submit(body: UpdateLoginFlowBody): Promise<boolean> {
    const flow = this.loginFlow();
    if (!flow) {
      return false;
    }
    const result = await submitFlow<LoginFlow, Session>(() =>
      this.kratos.completeLoginFlowV2(flow, body.method, body)
    );
    if (result.ok) {
      this.dialogRef.close(true);
      return true;
    }
    if (result.flow) {
      this.loginFlow.set(result.flow);
      this.messages.set(
        flowMessages(result.flow).filter(message => message.type === 'error')
      );
    } else {
      this.step.set('failed');
    }
    return false;
  }
}
